import { useCallback, useMemo } from "react";
import { IDelayCategory } from "@vmosolutions/oc-interfaces";
import { FormikSelectField } from "src/components/FormikComponents/FormikWrappers";
import { ISelectFieldProps } from "src/components/FormikComponents/SelectField";
// API Calls
import { formatFunction, parseFunction } from "./helpers";

import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";

interface IDelayCategorySelectorProps extends Omit<ISelectFieldProps, "options"> {
  mosaicPane: HTMLElement;
  valueKey?: string; // valueKey sent back as value - use entire object otherwise.
  showInactive?: boolean;
}

/** Sort types list Alphabetically */
const sortTypesByDescription = (a: IDelayCategory, b: IDelayCategory): number => {
  return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
};

/**
 * Sepcialized wrapper for "built-in" oc ATA select field.
 * @param props -
 */
export const DelayCategorySelector = (props: IDelayCategorySelectorProps): JSX.Element => {
  const { mosaicPane, label, name, onChange, valueKey, isMulti, defaultValue, ...selectProps } = props;

  const structures = useSelector((state: IRootState) => state.structures);
  const delayTypes = useMemo(() => {
    return structures?.customerStructure?.delayCategories ?? [];
  }, [structures?.customerStructure?.delayCategories]);

  const options = useMemo(() => delayTypes.sort(sortTypesByDescription), [delayTypes]);

  // Setup Format And Parse functions (used if valueKey is passed)
  const formatValue = useCallback(
    (value: string) => formatFunction(value, delayTypes, valueKey, isMulti),
    [delayTypes, valueKey, isMulti]
  );
  const parseOption = useCallback((option: string) => parseFunction(option, valueKey, isMulti), [valueKey, isMulti]);

  return (
    <FormikSelectField
      name={name}
      label={label ?? "Delay Category Selector"}
      isMulti={isMulti}
      options={options}
      getOptionLabel={(type: IDelayCategory) => `${type.title} - ${type.description}`}
      getOptionValue={(type: IDelayCategory) => type._id}
      format={(value) => (valueKey ? formatValue(value) : value)}
      parse={(option) => (valueKey ? parseOption(option) : option)}
      onChange={(value: any) => !!onChange && onChange(value)}
      menuPortalTarget={mosaicPane}
      menuPosition={mosaicPane ? "fixed" : undefined}
      isSearchable={true}
      openMenuOnFocus={false}
      isClearable={true}
      placeholder={`Select Delay Categor${isMulti ? "ies" : "y"}`}
      defaultValue={defaultValue}
      {...selectProps} // override with any props passed in such as isMulti, format, parse.
    />
  );
};
